import { companyForGrid } from '../../../spd/components/Company/CompanySearch';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import { DefaultApi } from '../autogenerated/spdApiClient/apis/DefaultApi';
import {
  CreateUpdateCompanyDto,
  CreateUpdateCompanyDtoFromJSON,
} from '../autogenerated/spdApiClient/models/CreateUpdateCompanyDto';
import { GetCompanyDto } from '../autogenerated/spdApiClient/models/GetCompanyDto';
import {
  Option,
  defaultSelectorLimit,
} from '../../../spd/components/Selects/Selector';
import { validateAgainst } from '../validation/validateAgainst';
import { CompanySchema } from '../validation/schemas';
import { GetLastEditorDto, GusClientDto } from '../autogenerated/spdApiClient';

export type CompanyEntity = GetCompanyDto;
export type Company = GetCompanyDto | CreateUpdateCompanyDto;

export interface CompanyContextInterface {
  get: (id: string) => Promise<CompanyEntity>;
  getMines: () => Promise<CompanyEntity[]>;
  getAll: () => Promise<CompanyEntity[]>;
  getAllForExternalReferral: () => Promise<CompanyEntity[]>;
  getAllByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    monthlySettlement?: boolean,
    invoicesSettlement?: boolean,
    externalReferral?: boolean,
  ) => Promise<GridRecord[]>;
  getAllCount: (
    filterText?: string,
    filterColumns?: string,
    monthlySettlement?: boolean,
    invoicesSettlement?: boolean,
    externalReferral?: boolean,
  ) => Promise<number>;
  getOptionsByName: (text: string) => Promise<Option[]>;
  getOptionsByNip: (text: string) => Promise<Option[]>;
  getOptionsByEmail: (text: string) => Promise<Option[]>;
  getOptionsByNameOrNip: (text: string) => Promise<Option[]>;
  update: (id: string, dto: CreateUpdateCompanyDto) => Promise<void>;
  create: (dto: CreateUpdateCompanyDto) => Promise<GetCompanyDto>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  deleteExpectationExams: (id: string) => Promise<void>;
  getByNip: (nip: string) => Promise<GusClientDto>;
}

export const CompanyContext = (api: DefaultApi) => {
  return {
    get: async (id: string) => {
      return await api.companyControllerGet(id);
    },

    getMines: async () => {
      return await api.companyControllerGetMine();
    },

    getAll: async () => {
      return await api.companyControllerGetAll();
    },

    getAllForExternalReferral: async () => {
      return await api.companyControllerGetAllForExternalReferral();
    },

    getAllByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      monthlySettlement?: boolean,
      invoicesSettlement?: boolean,
      externalReferral?: boolean,
    ) => {
      const data = await api.companyControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
        monthlySettlement,
        invoicesSettlement,
        externalReferral,
      );
      return data.map(companyForGrid);
    },

    getAllCount: (
      filterText?: string,
      filterColumns?: string,
      monthlySettlement?: boolean,
      invoicesSettlement?: boolean,
      externalReferral?: boolean,
    ) =>
      api.companyControllerGetAllCount(
        filterText || '',
        filterColumns,
        monthlySettlement,
        invoicesSettlement,
        externalReferral,
      ),

    getOptionsByName: async (text: string) => {
      const data = await api.companyControllerGetByPattern(
        text,
        defaultSelectorLimit,
      );

      return Array.from(data, company => {
        return {
          value: company.id,
          label: company.name + ' ' + (company.nip ? company.nip + ' ' : ''),
        };
      });
    },

    getOptionsByNip: async (text: string) => {
      const data = await api.companyControllerGetByNip(
        text,
        defaultSelectorLimit,
      );

      return Array.from(data, company => {
        return {
          value: company.id,
          label: company.nip + ' ' + company.name,
        };
      });
    },

    getOptionsByEmail: async (text: string) => {
      const data = await api.companyControllerGetByEmail(
        text,
        defaultSelectorLimit,
      );

      return Array.from(data, company => {
        return {
          value: company.id,
          label: company.email + ' (' + company.nip + ' ' + company.name + ')',
        };
      });
    },

    getOptionsByNameOrNip: async (text: string) => {
      const data = await api.companyControllerGetByNipOrName(
        text,
        defaultSelectorLimit,
      );
      return Array.from(data, company => {
        return {
          value: company.id,
          label:
            company.name + (company.nip ? ' (NIP: ' + company.nip + ')' : ''),
        };
      });
    },

    update: (id: string, dto: CreateUpdateCompanyDto) =>
      api.companyControllerUpdate(id, dto),

    create: (dto: CreateUpdateCompanyDto) => api.companyControllerCreate(dto),

    getLastEditor: (id: string) => api.companyControllerGetLastEditor(id),

    deleteExpectationExams: (id: string) =>
      api.companyControllerDeleteExpectationExams(id),

    getByNip: (nip: string) => api.gusApiControllerGetByNip(nip),
  };
};

export const validate = (test: CreateUpdateCompanyDto) =>
  validateAgainst(CompanySchema, test);

export const companyData = (
  company?: GetCompanyDto,
): CreateUpdateCompanyDto => {
  return CreateUpdateCompanyDtoFromJSON({
    ...company,
    branchId: company?.branch?.id,
    healthcareFacilityId: company?.healthcareFacility?.id,
    taxCountryCodeId: company?.taxCountryCode?.id,
    taxCountryCodeShortname: company?.taxCountryCode?.shortname,
  });
};

export const partial = (property: 'name' | 'nip' | 'email', value: string) => {
  const obj: Partial<Company> = {};
  obj[property] = value;
  return obj;
};
